import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { TabsList } from "@/components/ui/tabs";

export default function SettingsTabSlider({ children }) {
  const scrollerRef = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const update = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setCanLeft(el.scrollLeft > 4);
    setCanRight(max > 4 && el.scrollLeft < max - 4);
  }, []);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    update();
    el.addEventListener("scroll", update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    if (el.firstElementChild) ro.observe(el.firstElementChild);
    window.addEventListener("resize", update);
    return () => {
      el.removeEventListener("scroll", update);
      ro.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [update, children]);

  const scrollByDir = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const step = Math.max(160, Math.min(280, el.clientWidth * 0.65));
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  return (
    <div className="relative flex items-center gap-1 sm:gap-2" data-testid="settings-tab-slider">
      <button
        type="button"
        aria-label="Previous tabs"
        data-testid="settings-tab-prev"
        disabled={!canLeft}
        onClick={() => scrollByDir(-1)}
        className="shrink-0 w-8 h-8 sm:w-9 sm:h-9 rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm flex items-center justify-center disabled:opacity-30 disabled:pointer-events-none hover:bg-slate-50"
      >
        <ChevronLeft className="w-4 h-4" />
      </button>
      <div
        ref={scrollerRef}
        className="flex-1 min-w-0 overflow-x-auto no-scrollbar scroll-smooth [-webkit-overflow-scrolling:touch]"
        data-testid="settings-tab-scroller"
      >
        <TabsList data-testid="settings-tabs" className="inline-flex w-max max-w-none flex-nowrap h-auto min-h-9 whitespace-nowrap">
          {children}
        </TabsList>
      </div>
      <button
        type="button"
        aria-label="Next tabs"
        data-testid="settings-tab-next"
        disabled={!canRight}
        onClick={() => scrollByDir(1)}
        className="shrink-0 w-8 h-8 sm:w-9 sm:h-9 rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm flex items-center justify-center disabled:opacity-30 disabled:pointer-events-none hover:bg-slate-50"
      >
        <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );
}
